<script setup>
import { computed, reactive, ref, toRefs } from 'vue';
import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { isEmpty, isTelValid } from '@/common/utils/Util';
import { AUTHENTICATION_STATUS, initialize, phoneState } from '@/common/utils/module/AuthenticateSupport';
import router from '@/router';
import { showTextModal } from '@/common/utils/ModalState';
import store from '@/store';
import ServiceExec from '@/common/utils/ServiceExec';
import PhoneAuthInput from '@/components/common/input/PhoneAuthInput.vue';
import PatternLock from '@/components/common/input/PatternLock.vue';

// 260922 대표(유저 전달): "마이노트(쉼) 비번 새로 설정하게끔 팝업창 + 지문이나 패턴인식".
//   - 한 모달에 2단계: verify(잠금 해제) / setup(새로 설정). 옛 흐름은 재설정 = 페이지 이동(/setting/welldying/password)이라 '팝업'이 아니었다.
//   - 잠금 수단 = 숫자 6자리 | 패턴(3×3, PatternLock). 패턴은 점 순서 숫자열을 기존 웰다잉 비밀번호(bcrypt·서버 검증)에 그대로 태운다 → BE 계약·DDL 변경 0.
//     어느 수단으로 잠갔는지는 이 기기 localStorage 힌트로만 기억(탭 기본값). 다른 기기에선 탭을 골라 입력하면 된다.
//   - 새로 설정 = 휴대폰 문자 인증(가입 번호) 후 저장. 생년월일 입력은 뺐다 — 소셜 가입자 52명은 생년월일 NULL 이라 옛 페이지에선 재설정이 불가능했다(BE 도 null-safe 로 같이 수정).
//   - 지문/Face ID 는 WebView 라 네이티브 브릿지(3플랫폼)가 필요 → 별건.
const LOCK_KEY = 'jump_well_lock_type';
const NUMBER_LEN = 6;

const props = defineProps(['data']);
const { url, mode } = toRefs(props.data || {});
const emits = defineEmits(['close']);
const user = computed(() => store.getters['user/getUserData']);

const readLockType = () => {
  try { return localStorage.getItem(LOCK_KEY) === 'pattern' ? 'pattern' : 'number'; } catch (e) { return 'number'; }
};
const saveLockType = t => { try { localStorage.setItem(LOCK_KEY, t); } catch (e) { /* 프라이빗 모드 등 — 힌트일 뿐 */ } };

const step = ref(mode?.value === 'setup' ? 'setup' : 'verify');
const lockType = ref(readLockType());
const busy = ref(false);

// ── verify ──
const inputPassword = ref('');
const patternRef = ref(null);
const v$ = useVuelidate({
  inputPassword: { isEmpty: helpers.withMessage('웰다잉 비밀번호를 입력해 주세요.', value => !isEmpty(value)) }
}, { inputPassword });

// 260628: wellPassword는 bcrypt 단방향 해시(260521 전환)라 클라 평문 비교 불가 → 서버 검증(passwordEncoder.matches). 패턴도 같은 경로.
const verify = async secret => {
  if (busy.value) return false;
  busy.value = true;
  try {
    const { status, data } = await ServiceExec.post('/api/user/verify/well-password', { userNo: user.value.userNo, wellPassword: secret });
    return status === ServiceExec.status.OK && data === true;
  } finally { busy.value = false; }
};
const unlock = () => {
  emits('close');
  if (url?.value) router.replace(url.value);
  else router.push('/welldying/menu');
};
const fnConfirm = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (await verify(inputPassword.value)) { saveLockType('number'); unlock(); return; }
  showTextModal('비밀번호가 일치하지 않습니다.', '확인');
};
const onPatternVerify = async seq => {
  if (await verify(seq)) { saveLockType('pattern'); unlock(); return; }
  patternRef.value?.reset('패턴이 일치하지 않습니다. 다시 그려 주세요.');
};

// ── setup (새로 설정) ──
const newNumber = ref('');
const newNumber2 = ref('');
const patternFirst = ref('');
const patternSetRef = ref(null);
const patternHint = computed(() => (patternFirst.value ? '확인을 위해 같은 패턴을 한 번 더 그려 주세요.' : '새 패턴을 그려 주세요. (점 4개 이상)'));
const setupError = ref('');

// SetupWellPassword.vue 와 같은 SMS 본인확인 — 가입 번호로만 발송(findIdAuthCode 는 userId 기준). PhoneAuthInput 이 vuelidate 객체를 요구해 규칙 유지.
const phoneForm = reactive({ phone: '', code: '+82' });
const phoneCodeValid = reactive({ phoneCode: { text: '' } });
const phone$ = useVuelidate({
  phone: {
    isValid: helpers.withMessage("'-'제외 10 ~ 11자 이내의 숫자를 입력해 주세요.", value => isTelValid(value)),
    isTel: helpers.withMessage('유효하지 않은 전화번호입니다.', (val, form) => {
      if (!/^\d+$/.test(val) || val.length < 10 || val.length > 11) return false;
      if (form.code === '+1') return true;
      return ['010', '011', '016', '017', '018', '019'].includes(val.substring(0, 3));
    })
  }
}, phoneForm);
const authInit = () => {
  store.commit('user/setFindUserId', user.value.userId);
  initialize('03:00', () => store.dispatch('user/findIdAuthCode', { phone: phoneForm.phone, code: phoneForm.code }));
};

const goSetup = () => { step.value = 'setup'; setupError.value = ''; };
const pickType = t => { lockType.value = t; setupError.value = ''; patternFirst.value = ''; patternSetRef.value?.reset(); };
const onPatternDraw = seq => {
  if (!patternFirst.value) { patternFirst.value = seq; patternSetRef.value?.reset(); return; }
  if (seq !== patternFirst.value) { patternFirst.value = ''; patternSetRef.value?.reset('두 패턴이 다릅니다. 처음부터 다시 그려 주세요.'); return; }
  savePassword(seq);
};
const fnSaveNumber = () => {
  if (!/^\d{6}$/.test(newNumber.value)) { setupError.value = `비밀번호는 숫자 ${NUMBER_LEN}자리입니다.`; return; }
  if (newNumber.value !== newNumber2.value) { setupError.value = '두 비밀번호가 다릅니다.'; return; }
  savePassword(newNumber.value);
};
const savePassword = async secret => {
  if (phoneState.value !== AUTHENTICATION_STATUS.COMPLETED) {
    phoneCodeValid.phoneCode.text = '휴대전화 문자 인증을 먼저 완료해 주십시오.';
    setupError.value = '휴대전화 문자 인증을 먼저 완료해 주십시오.';
    if (lockType.value === 'pattern') { patternFirst.value = ''; patternSetRef.value?.reset(); }
    return;
  }
  if (busy.value) return;
  busy.value = true;
  try {
    const birth = user.value?.birthDate ? String(user.value.birthDate).slice(0, 10) : null;
    const response = await ServiceExec.patch('/api/user/update/well-password', { userNo: user.value.userNo, birthDate: birth, wellPassword: secret });
    if (response?.status !== ServiceExec.status.OK) { showTextModal(response?.data || '저장에 실패했습니다. 다시 시도해 주십시오.', '확인'); return; }
    store.commit('user/setHasWellPassword', true);
    saveLockType(lockType.value);
    showTextModal(lockType.value === 'pattern' ? '패턴이 설정되었습니다.' : '비밀번호가 설정되었습니다.', '확인', () => unlock());
  } finally { busy.value = false; }
};

const fnClose = () => {
  if (url?.value) router.replace({ path: '/intro', query: { isRoot: 1 } });
  else emits('close');
};
</script>

<template>
  <div class="modal-dim on">
    <div class="modal-wrap">
      <div class="modal-inner center">
        <div class="modal wp-modal">
          <div class="modal-head">
            <button class="header-btn" @click="fnClose"><i class="ico-close-gray90"></i></button>
          </div>
          <div class="modal-con wp-scroll">
            <!-- ── 잠금 해제 ── -->
            <template v-if="step === 'verify'">
              <h2 class="wp-title">마이노트(쉼) 잠금 해제</h2>
              <div class="wp-tabs" role="tablist">
                <button type="button" class="wp-tab" :class="{ 'is-on': lockType === 'number' }" role="tab" :aria-selected="lockType === 'number'" @click="lockType = 'number'">숫자 비밀번호</button>
                <button type="button" class="wp-tab" :class="{ 'is-on': lockType === 'pattern' }" role="tab" :aria-selected="lockType === 'pattern'" @click="lockType = 'pattern'">패턴</button>
              </div>
              <div v-if="lockType === 'number'" class="form">
                <ul class="form-ul">
                  <li class="form-li">
                    <div class="form-ttl essen"><span>웰다잉 비밀번호 입력</span></div>
                    <div class="form-in">
                      <label class="label">
                        <input type="password" inputmode="numeric" v-model="inputPassword" :maxlength="NUMBER_LEN" placeholder="비밀번호 숫자 6자리를 입력해 주세요." @keyup.enter="fnConfirm" />
                      </label>
                    </div>
                    <div class="valid" :class="v$.inputPassword.$error ? 'on' : ''">{{ v$.inputPassword.isEmpty.$message }}</div>
                  </li>
                </ul>
              </div>
              <PatternLock v-else ref="patternRef" hint="설정한 패턴을 그려 주세요." aria-label="패턴 잠금 해제" @complete="onPatternVerify" />
              <!-- .stop: 전역 컨테이너(views/index.vue modalOutSideEvent)가 루트 click 에서 "대상이 .modal 안인가"를 보는데, 이 버튼은 클릭 즉시 재렌더로 DOM 에서 빠져 바깥 클릭으로 오판 → 모달이 닫혔다(260922 유저 "재설정하기가 안 먹힘"). -->
              <button type="button" class="wp-link" @click.stop="goSetup">비밀번호를 잊으셨나요? 새로 설정하기</button>
            </template>

            <!-- ── 새로 설정 ── -->
            <template v-else>
              <h2 class="wp-title">마이노트(쉼) 잠금 새로 설정</h2>
              <p class="wp-desc">가입한 휴대전화로 문자 인증 후 새 잠금을 설정합니다.</p>
              <div class="form">
                <ul class="form-ul">
                  <li class="form-li">
                    <PhoneAuthInput v-model="phoneForm" :validation="phone$" :custom-valid="phoneCodeValid.phoneCode" :auth-init-call="authInit" error-text="회원정보에 등록한 휴대전화 번호를 입력해 주세요." />
                  </li>
                </ul>
              </div>
              <div class="wp-tabs" role="tablist">
                <button type="button" class="wp-tab" :class="{ 'is-on': lockType === 'number' }" role="tab" :aria-selected="lockType === 'number'" @click="pickType('number')">숫자 6자리</button>
                <button type="button" class="wp-tab" :class="{ 'is-on': lockType === 'pattern' }" role="tab" :aria-selected="lockType === 'pattern'" @click="pickType('pattern')">패턴</button>
              </div>
              <div v-if="lockType === 'number'" class="form">
                <ul class="form-ul">
                  <li class="form-li">
                    <div class="form-ttl essen"><span>새 비밀번호</span></div>
                    <div class="form-in"><label class="label"><input type="password" inputmode="numeric" v-model="newNumber" :maxlength="NUMBER_LEN" placeholder="숫자 6자리" /></label></div>
                  </li>
                  <li class="form-li">
                    <div class="form-ttl essen"><span>새 비밀번호 확인</span></div>
                    <div class="form-in"><label class="label"><input type="password" inputmode="numeric" v-model="newNumber2" :maxlength="NUMBER_LEN" placeholder="한 번 더 입력" @keyup.enter="fnSaveNumber" /></label></div>
                  </li>
                </ul>
              </div>
              <PatternLock v-else ref="patternSetRef" :hint="patternHint" aria-label="새 패턴 설정" @complete="onPatternDraw" />
              <p v-if="setupError" class="wp-error" role="alert">{{ setupError }}</p>
            </template>
          </div>
          <div class="modal-btn">
            <div class="btn-wrap" v-if="step === 'verify'">
              <button class="btn medium border-gray" @click="fnClose">닫기</button>
              <button v-if="lockType === 'number'" class="btn primary medium" :disabled="busy" @click="fnConfirm">확인</button>
            </div>
            <div class="btn-wrap" v-else>
              <button v-if="mode !== 'setup'" class="btn medium border-gray" @click.stop="step = 'verify'">이전</button>
              <button v-else class="btn medium border-gray" @click="fnClose">닫기</button>
              <button v-if="lockType === 'number'" class="btn primary medium" :disabled="busy" @click="fnSaveNumber">저장</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 최소 골격 — 시각(탭·타이틀 톤)은 코덱스 외주 후보. 40~50대: 제목 18px+, 탭 큼직 */
.wp-modal { max-height: 90vh; display: flex; flex-direction: column; }
.wp-scroll { overflow-y: auto; -webkit-overflow-scrolling: touch; }
.wp-title { margin: 0 0 0.6rem; font-size: 1.9rem; font-weight: 700; letter-spacing: -0.02em; color: #1f212a; text-align: center; }
.wp-desc { margin: 0 0 1.2rem; font-size: 1.4rem; line-height: 1.5; color: #6b7280; text-align: center; }
.wp-tabs { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; margin: 0.4rem 0 1.4rem; padding: 0.4rem; border-radius: 1.2rem; background: #f0f2f7; }
.wp-tab { min-height: 4.4rem; border: 0; border-radius: 0.9rem; background: transparent; font-size: 1.5rem; font-weight: 700; color: #6b7280; }
.wp-tab.is-on { background: #fff; color: #1f212a; box-shadow: 0 0.1rem 0.4rem rgba(0, 0, 0, 0.08); }
.wp-link { display: block; width: 100%; margin-top: 1.2rem; padding: 1rem 0; border: 0; background: transparent; font-size: 1.4rem; font-weight: 600; color: #3182f6; text-decoration: underline; text-underline-offset: 0.3rem; }
.wp-error { margin: 0.8rem 0 0; font-size: 1.4rem; color: #ff3b30; text-align: center; }
</style>
